import { type FC, useState, useCallback } from 'react';
import { graphql, useFragment } from 'react-relay';
import classnames from 'classnames';
import dibsCss from 'dibs-css';

// components
import { Link } from 'dibs-elements/exports/Link';
import { FormattedMessage } from 'dibs-react-intl';
import { learnMore } from '../messages/pdpBioMessages';
import { SharedStarRating } from '../../shared/SharedStarRating/SharedStarRating';
import { PdpSharedNewSellerPill } from '../shared/PdpSharedNewSellerPill/PdpSharedNewSellerPill';
import { PdpSharedDealerDetailsFeedback } from '../PdpSharedDealerDetailsFeedback/PdpSharedDealerDetailsFeedback';
import { PdpSharedDealerDetailsBioModalLazy } from '../PdpSharedDealerDetailsBio/PdpSharedDealerDetailsBioModalLazy';

// helpers
import { getIsNewSeller } from '../../utils/sellerHelpers';
import { getSrcsetString } from 'dibs-image-utils/exports/srcSet';
import {
    goldSeller,
    platinumSeller,
    diamondSeller,
    vettedSeller,
    defaultSeller,
} from '../messages/pdpDistinctionsMessages';

// types
import {
    type PdpSharedDealerDetailsHeader_seller$key as Seller,
    type PdpSharedDealerDetailsHeader_seller$data,
} from './__generated__/PdpSharedDealerDetailsHeader_seller.graphql';
import { type PdpSharedDealerDetailsHeader_user$key as User } from './__generated__/PdpSharedDealerDetailsHeader_user.graphql';

// styles
import styles from './PdpSharedDealerDetailsHeader.scss';

const shouldShowBioModal = (seller: PdpSharedDealerDetailsHeader_seller$data): boolean => {
    let hasVideo = false;
    const isRewardsTier2 = seller?.isRewardsTier2;
    const mediaList =
        seller?.liveStorefrontProfile?.galleryAlbum?.mediaList?.filter(media => {
            const element = media?.element;
            const { url, path } = element || {};
            if (url) {
                hasVideo = true;
            }
            return path || url;
        }) || [];

    return !isRewardsTier2 && (hasVideo || mediaList.length > 1);
};

const userFragment = graphql`
    fragment PdpSharedDealerDetailsHeader_user on User {
        canShowSellerName
    }
`;

const sellerFragment = graphql`
    fragment PdpSharedDealerDetailsHeader_seller on Seller
    @argumentDefinitions(isExtended: { type: "Boolean", defaultValue: false }) {
        ...SharedStarRating_seller
        ...PdpSharedDealerDetailsFeedback_seller
        ...PdpSharedDealerDetailsBioModalLazy_seller @arguments(isExtended: $isExtended)
        isRewardsTier2: isAnchorListMember(listName: "SELLER_REWARDS_2")
        monthsSinceStartDate: timeSinceStartDate(unit: MONTH)
        sellerProfile {
            company
        }
        shippingAddress {
            displayFullCityStateCountry: displayCityStateCountry(fullCountryName: true)
        }
        sellerTiers {
            isGoldTier
            isPlatinumTier
            sellerTierDataNotFound
        }
        liveStorefrontProfile {
            headshots: photos(photoType: DEALER_STOREFRONT_HEADSHOT) {
                status
                path
            }
            galleryAlbum {
                mediaList {
                    element {
                        ... on Photo {
                            path
                        }
                        ... on Video {
                            url
                        }
                    }
                }
            }
        }
        dealerDetailsHeaderFeedback: orderFeedbackPdp(
            first: 3
            minimumReviewRating: 4
            feedbackSellerPublicNoteHasText: true
        ) {
            edges {
                node {
                    __typename
                }
            }
        }
    }
`;

type Props = {
    user: User | null | undefined;
    seller: Seller;
    afterDisplay: boolean;
    isMobile?: boolean;
    inlineHeader?: boolean;
    inlineBioLink?: boolean;
    isCompact?: boolean;
    hideTitle?: boolean;
};

export const PdpSharedDealerDetailsHeader: FC<Props> = ({
    isMobile,
    afterDisplay,
    user: userRef,
    seller: sellerRef,
    inlineHeader = true,
    inlineBioLink = false,
    isCompact = false,
}) => {
    const user = useFragment(userFragment, userRef);
    const seller = useFragment(sellerFragment, sellerRef);
    const {
        isRewardsTier2,
        sellerTiers,
        shippingAddress,
        monthsSinceStartDate,
        liveStorefrontProfile,
    } = seller || {};
    const canShowSellerName = afterDisplay && user?.canShowSellerName;
    const isNewSeller = getIsNewSeller(monthsSinceStartDate);
    const dealerLocation = shippingAddress?.displayFullCityStateCountry;
    const { headshots, galleryAlbum } = liveStorefrontProfile || {};
    const { status: headshotStatus, path: headshotPath } = headshots?.[0] || {};
    const showHeadshot = isRewardsTier2 && headshotStatus === 'ACTIVE' && !!headshotPath;
    const mediaList = galleryAlbum?.mediaList || [];
    const reviewHighlights = seller?.dealerDetailsHeaderFeedback?.edges;
    const reviewStarsBelow =
        isCompact && isMobile && reviewHighlights && reviewHighlights?.length > 0;

    const { sellerTierDataNotFound, isGoldTier, isPlatinumTier } = sellerTiers || {};
    const sellerTierDataFound = !sellerTierDataNotFound;
    const isGoldSeller = sellerTierDataFound && isGoldTier;
    const isPlatinumSeller = sellerTierDataFound && isPlatinumTier;

    const [isBioModalOpen, setIsBioModalOpen] = useState(false);

    let sellerImagePath;

    if (showHeadshot) {
        sellerImagePath = headshotPath;
    } else if (mediaList.length) {
        sellerImagePath = mediaList.find(media => media?.element?.path)?.element?.path;
    }

    const isCompactMobile = isCompact && isMobile;

    let compactSellerTitle = defaultSeller;
    if (isCompactMobile) {
        if (isGoldSeller) {
            compactSellerTitle = goldSeller;
        } else if (isPlatinumSeller) {
            compactSellerTitle = platinumSeller;
        } else if (isRewardsTier2) {
            compactSellerTitle = diamondSeller;
        } else {
            compactSellerTitle = vettedSeller;
        }
    }

    const detailsHeader = (
        <div
            data-tn="pdp-shared-dealer-details-header"
            className={classnames(styles.headerPrimary, {
                [styles.small]: isCompact,
                [styles.noPadding]: reviewStarsBelow,
            })}
        >
            <span
                className={classnames(styles.sellerTitle, {
                    [styles.spaceRight]: isNewSeller,
                })}
            >
                {canShowSellerName ? (
                    seller?.sellerProfile?.company
                ) : (
                    <FormattedMessage
                        id="PdpSharedDealerDetailsHeader.seller_title"
                        defaultMessage="{isCompactMobile, select,
                                true {{compactSellerTitle}}
                                other {About the Seller}
                            }"
                        values={{
                            isCompactMobile,
                            compactSellerTitle,
                        }}
                    />
                )}
            </span>
            {isNewSeller && (
                <span
                    className={classnames(styles.pill, dibsCss.mlXxsmall, {
                        [styles.align]: !isCompact,
                    })}
                >
                    <PdpSharedNewSellerPill
                        backgroundColor={isMobile ? 'buyer-tertiary' : 'white'}
                    />
                </span>
            )}
        </div>
    );

    // TOF-18: Image stretched vertically - https://1stdibs.atlassian.net/browse/TOF-18
    if (sellerImagePath && sellerImagePath?.includes('height')) {
        const imageUrl = new URL(sellerImagePath);
        const urlParams = new URLSearchParams(imageUrl.search);
        urlParams.delete('height');
        sellerImagePath = `${imageUrl.origin}${imageUrl.pathname}?${urlParams.toString()}`;
    }

    const sellerImage = sellerImagePath ? (
        <img
            alt="" // decorative image
            sizes="(max-width:768px) 60px, 80px, 100px"
            src={sellerImagePath}
            className={styles.image}
            srcSet={getSrcsetString(sellerImagePath, [100, 200, 300])}
        />
    ) : null;

    const reviewTotalContainer = (
        <div className={isCompact ? styles.reviewsCompact : styles.reviews}>
            <div className={styles.reviewsStarRating}>
                <SharedStarRating size={isCompact ? 'medium' : 'small'} seller={seller} />
            </div>
            <PdpSharedDealerDetailsFeedback seller={seller} isMobile={!!isMobile} />
        </div>
    );

    const openBioModal = useCallback(() => setIsBioModalOpen(true), []);
    const closeBioModal = useCallback(() => setIsBioModalOpen(false), []);

    return (
        <>
            {!inlineHeader && <div className={styles.inlineHeader}>{detailsHeader}</div>}
            <div
                className={classnames(styles.container, {
                    [styles.reviewStarsBelow]: reviewStarsBelow,
                })}
            >
                {sellerImage &&
                    ((shouldShowBioModal(seller) && !inlineBioLink) || isCompact ? (
                        <Link
                            onClick={openBioModal}
                            className={classnames(styles.imageContainer, {
                                [styles.compact]: isCompact,
                            })}
                        >
                            {sellerImage}
                        </Link>
                    ) : (
                        <div
                            className={classnames(styles.imageContainer, {
                                [styles.compact]: isCompact,
                            })}
                        >
                            {sellerImage}
                        </div>
                    ))}
                <div className={styles.header}>
                    {inlineHeader && detailsHeader}
                    {!reviewStarsBelow && reviewTotalContainer}
                    <span className={styles.location} data-tn="dealer-location">
                        <FormattedMessage
                            id="PdpSharedDealerDetailsHeader.location"
                            defaultMessage="Located in {dealerLocation}"
                            values={{ dealerLocation }}
                        />
                    </span>
                    {afterDisplay && (shouldShowBioModal(seller) || isCompact) && (
                        <>
                            {inlineBioLink && (
                                <Link className={styles.learnMore} onClick={openBioModal}>
                                    {learnMore}
                                </Link>
                            )}
                            <PdpSharedDealerDetailsBioModalLazy
                                seller={seller}
                                isOpen={isBioModalOpen}
                                onClose={closeBioModal}
                                isExtended={isCompact}
                            />
                        </>
                    )}
                </div>
            </div>
            {reviewStarsBelow && reviewTotalContainer}
        </>
    );
};
